import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import axiosInstance from "../../axiosInstance";
import getDecryptData from "../../helpers/getDecryptData";
import { getJobPortalAuthConfig } from "../../helpers/jobPortalAuth";
import PublicLayout from "../components/PublicLayout";
import PublicJobCard from "../components/PublicJobCard";

const PublicJobDetail = () => {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [savedJobs, setSavedJobs] = useState([]);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedJobs, setRelatedJobs] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const user = await getDecryptData();
        setSession(user || null);

        const requests = [axiosInstance.get(`/job-portal/jobs/public/${id}`)];
        if (user?.type === 2) {
          const authConfig = await getJobPortalAuthConfig();
          requests.push(axiosInstance.get("/job-portal/jobs/saved", authConfig));
        }

        const [jobResponse, savedResponse] = await Promise.all(requests);
        const currentJob = jobResponse.data?.data?.job || null;
        setJob(currentJob);
        setSavedJobs(savedResponse?.data?.data?.jobs?.map((item) => item.id) || []);

        if (currentJob) {
          const relatedResponse = await axiosInstance.get(
            `/job-portal/jobs/public?limit=3&department=${encodeURIComponent(
              currentJob.department || ""
            )}&recruiterId=${currentJob.recruiter?.id || ""}`
          );
          const nextJobs = (relatedResponse.data?.data?.jobs || []).filter((item) => item.id !== currentJob.id);
          setRelatedJobs(nextJobs.slice(0, 3));
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id]);

  const isSeeker = Number(session?.type) === 2;
  const isSaved = useMemo(() => savedJobs.includes(job?.id), [job?.id, savedJobs]);

  const handleApply = async () => {
    if (!isSeeker) {
      window.location.href = "/login";
      return;
    }

    try {
      const authConfig = await getJobPortalAuthConfig();
      await axiosInstance.post(`/job-portal/jobs/${job.id}/apply`, {}, authConfig);
      toast.success("Application submitted successfully");
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to apply");
    }
  };

  const handleSave = async () => {
    if (!isSeeker) {
      window.location.href = "/login";
      return;
    }

    try {
      const authConfig = await getJobPortalAuthConfig();
      if (isSaved) {
        const response = await axiosInstance.delete(`/job-portal/jobs/${job.id}/save`, authConfig);
        setSavedJobs(response.data?.data?.savedJobIds || []);
        toast.success("Removed from saved jobs");
      } else {
        const response = await axiosInstance.put(`/job-portal/jobs/${job.id}/save`, {}, authConfig);
        setSavedJobs(response.data?.data?.savedJobIds || []);
        toast.success("Saved job");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to update saved jobs");
    }
  };

  return (
    <PublicLayout>
      <Helmet>
        <title>{job ? `${job.title} | Job Detail` : "Job Details"}</title>
        <meta
          name="description"
          content={job?.description || "View full job details and apply on our professional job portal."}
        />
      </Helmet>

      <section className="portal-hero border-bottom text-start">
        <div className="portal-container">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status"></div>
            </div>
          ) : !job ? (
            <div className="text-center py-5 bg-light rounded">
              <h2 className="h4 fw-bold">Job Not Found</h2>
              <Link to="/jobs" className="portal-btn-primary mt-3">Back to All Jobs</Link>
            </div>
          ) : (
            <div className="py-3">
              <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
                <div className="portal-badge">{(job.employmentType || "full_time").replaceAll("_", " ")}</div>
                <span className="small portal-muted fw-bold text-uppercase">{job.workMode || "onsite"}</span>
              </div>
              <h1 className="portal-title mb-3">{job.title}</h1>
              <div className="d-flex align-items-center gap-3 portal-muted mb-3 flex-wrap">
                <span className="fw-bold text-dark">{job.company?.name || "Hiring Company"}</span>
                <span>|</span>
                <span>{job.location?.display || "India"}</span>
                <span>|</span>
                <span>{job.company?.size || "Growing team"}</span>
              </div>

              <div className="portal-job-tags my-0">
                <span className="portal-pill">Salary: {job.salary?.minimum || 0} - {job.salary?.maximum || 0}</span>
                <span className="portal-pill">Experience: {job.experience?.minimum || 0} - {job.experience?.maximum || 0} years</span>
                <span className="portal-pill">Qualification: {job.qualification || "Open"}</span>
              </div>
            </div>
          )}
        </div>
      </section>

      {!loading && job ? (
        <section className="portal-section">
          <div className="portal-container">
            <div className="row gy-4">
              <div className="col-lg-8">
                <div className="d-grid gap-3">
                  <div className="detail-section">
                    <h2 className="h5 fw-bold mb-2">About the role</h2>
                    <p className="portal-muted mb-0">{job.description || "A detailed role description will be provided shortly."}</p>
                  </div>

                  {job.requirements ? (
                    <div className="detail-section">
                      <h2 className="h5 fw-bold mb-2">Key requirements</h2>
                      <div className="portal-muted" style={{ whiteSpace: "pre-line" }}>{job.requirements}</div>
                    </div>
                  ) : null}

                  {job.responsibilities ? (
                    <div className="detail-section">
                      <h2 className="h5 fw-bold mb-2">Responsibilities</h2>
                      <div className="portal-muted" style={{ whiteSpace: "pre-line" }}>{job.responsibilities}</div>
                    </div>
                  ) : null}

                  <div className="detail-section">
                    <h2 className="h5 fw-bold mb-2">Core skills</h2>
                    <div className="portal-job-tags mb-0">
                      {(job.skills || []).map((skill) => (
                        <span className="portal-pill" key={skill}>{skill}</span>
                      ))}
                    </div>
                  </div>
                </div>

                {relatedJobs.length > 0 ? (
                  <div className="mt-5">
                    <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                      <h2 className="h5 fw-bold mb-0">Similar opportunities</h2>
                      <Link to="/jobs" className="portal-btn-secondary">View All</Link>
                    </div>
                    <div className="portal-job-grid">
                      {relatedJobs.map((item) => (
                        <PublicJobCard key={item.id} job={item} />
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>

              <aside className="col-lg-4">
                <div className="d-grid gap-3">
                  <div className="portal-card p-3">
                    <h3 className="h6 fw-bold mb-3">Apply for this role</h3>
                    <div className="d-grid gap-2">
                      <button className="portal-btn-primary w-100" onClick={handleApply}>Submit Application</button>
                      <button className="portal-btn-secondary w-100" onClick={handleSave}>
                        {isSaved ? "Saved" : "Save for Later"}
                      </button>
                    </div>
                  </div>

                  <div className="portal-card p-3">
                    <h3 className="h6 fw-bold mb-2">Company insights</h3>
                    <div className="fw-bold">{job.company?.name}</div>
                    <p className="portal-muted small mb-3">{job.company?.address || "Location verified"}</p>
                    {job.recruiter?.profileUrl ? (
                      <Link to={`/companies/${job.recruiter.profileUrl}`} className="portal-btn-secondary w-100">
                        Explore Company Profile
                      </Link>
                    ) : null}
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>
      ) : null}
    </PublicLayout>
  );
};

export default PublicJobDetail;
